import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  firestore, collection, getDocs, query, orderBy
} from "../firebase-config";
import Layout from "../layouts/Layout";
import "../styles/OnlineSermons.css";

const OnlineSermons = () => {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, series, speakers, scriptures
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSermons = async () => {
      try {
        const sermonsQuery = query(
          collection(firestore, "sermons"),
          orderBy("date", "desc")
        );
        const snapshot = await getDocs(sermonsQuery);
        const sermonsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSermons(sermonsData);
      } catch (error) {
        console.error("Error fetching sermons:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSermons();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/
    );
    return match ? match[1] : null;
  };

  const getThumbnail = (sermon) => {
    if (sermon.thumbnailUrl) return sermon.thumbnailUrl;
    const ytId = getYouTubeId(sermon.youtubeUrl);
    if (ytId) return `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`;
    return null;
  };

  // Get unique values for filter tabs
  const uniqueSeries = [...new Set(sermons.filter(s => s.series).map(s => s.series))];
  const uniqueSpeakers = [...new Set(sermons.filter(s => s.speaker).map(s => s.speaker))];

  // Scripture-aware search: checks if a searched verse falls within a stored range
  // Handles multiple references separated by ; or ,
  const scriptureMatchesSearch = (scripture, search) => {
    if (!scripture || !search) return false;
    const lower = scripture.toLowerCase();
    const searchLower = search.toLowerCase();

    // Simple includes check first
    if (lower.includes(searchLower)) return true;

    // Split both the stored scripture and the search term on ; and ,
    const storedRefs = lower.split(/[;,]/).map(s => s.trim()).filter(Boolean);
    const searchRefs = searchLower.split(/[;,]/).map(s => s.trim()).filter(Boolean);

    const rangeRegex = /^(.+?)\s*(\d+):(\d+)\s*[-–—]\s*(\d+)$/;
    const singleRegex = /^(.+?)\s*(\d+):(\d+)$/;
    const chapterOnlyRegex = /^(.+?)\s*(\d+)$/;

    // Check if any search reference matches any stored reference
    for (const searchRef of searchRefs) {
      for (const storedRef of storedRefs) {
        // Direct includes
        if (storedRef.includes(searchRef) || searchRef.includes(storedRef)) return true;

        // Parse stored as a range, search as a single verse
        const storedRangeMatch = storedRef.match(rangeRegex);
        const searchSingleMatch = searchRef.match(singleRegex);

        if (storedRangeMatch && searchSingleMatch) {
          const [, sBook, sChapter, sStart, sEnd] = storedRangeMatch;
          const [, qBook, qChapter, qVerse] = searchSingleMatch;

          if (
            sBook.trim() === qBook.trim() &&
            sChapter === qChapter &&
            parseInt(qVerse) >= parseInt(sStart) &&
            parseInt(qVerse) <= parseInt(sEnd)
          ) {
            return true;
          }
        }

        // Parse both as ranges (overlap check)
        const searchRangeMatch = searchRef.match(rangeRegex);
        if (storedRangeMatch && searchRangeMatch) {
          const [, sBook, sChapter, sStart, sEnd] = storedRangeMatch;
          const [, qBook, qChapter, qStart, qEnd] = searchRangeMatch;

          if (
            sBook.trim() === qBook.trim() &&
            sChapter === qChapter &&
            parseInt(qStart) <= parseInt(sEnd) &&
            parseInt(qEnd) >= parseInt(sStart)
          ) {
            return true;
          }
        }

        // Search is a single verse, stored is a single verse (exact match already covered by includes)
        // Search is a range, stored is a single verse
        if (searchRangeMatch && !storedRangeMatch) {
          const storedSingleMatch = storedRef.match(singleRegex);
          if (storedSingleMatch) {
            const [, qBook, qChapter, qStart, qEnd] = searchRangeMatch;
            const [, sBook, sChapter, sVerse] = storedSingleMatch;

            if (
              sBook.trim() === qBook.trim() &&
              sChapter === qChapter &&
              parseInt(sVerse) >= parseInt(qStart) &&
              parseInt(sVerse) <= parseInt(qEnd)
            ) {
              return true;
            }
          }
        }
      }
    }

    return false;
  };

  // Filter sermons based on active filter and search
  const filteredSermons = sermons.filter((sermon) => {
    const matchesSearch =
      !searchTerm ||
      sermon.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sermon.speaker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sermon.series?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sermon.scripture?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scriptureMatchesSearch(sermon.scripture, searchTerm);

    return matchesSearch;
  });

  return (
    <Layout featureType="online_sermons">
      <div className="sermons-page">
        <h2>Sermons</h2>
        <p className="sermons-intro">
          Listen to or watch our sermons. Click on a sermon to listen or watch.
        </p>

        {/* Filter Tabs */}
        <div className="sermons-tabs">
          <button
            className={`tab-btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All Sermons
          </button>
          <button
            className={`tab-btn ${filter === "series" ? "active" : ""}`}
            onClick={() => setFilter("series")}
          >
            Series
          </button>
          <button
            className={`tab-btn ${filter === "speakers" ? "active" : ""}`}
            onClick={() => setFilter("speakers")}
          >
            Speakers
          </button>
        </div>

        {/* Search */}
        <div className="sermons-search">
          <input
            type="text"
            placeholder="Search sermons by title, speaker, series, or scripture..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Content based on active filter */}
        {loading ? (
          <p className="loading-text">Loading sermons...</p>
        ) : filter === "series" ? (
          <div className="filter-list">
            <h3>Browse by Series</h3>
            <div className="filter-tags">
              {uniqueSeries.length > 0 ? (
                uniqueSeries.map((series) => (
                  <button
                    key={series}
                    className="filter-tag"
                    onClick={() => {
                      setSearchTerm(series);
                      setFilter("all");
                    }}
                  >
                    {series}
                  </button>
                ))
              ) : (
                <p>No series found.</p>
              )}
            </div>
          </div>
        ) : filter === "speakers" ? (
          <div className="filter-list">
            <h3>Browse by Speaker</h3>
            <div className="filter-tags">
              {uniqueSpeakers.length > 0 ? (
                uniqueSpeakers.map((speaker) => (
                  <button
                    key={speaker}
                    className="filter-tag"
                    onClick={() => {
                      setSearchTerm(speaker);
                      setFilter("all");
                    }}
                  >
                    {speaker}
                  </button>
                ))
              ) : (
                <p>No speakers found.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="sermons-grid">
            {filteredSermons.length > 0 ? (
              filteredSermons.map((sermon) => (
                <div
                  key={sermon.id}
                  className="sermon-card"
                  onClick={() => navigate(`/online-sermons/${sermon.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/online-sermons/${sermon.id}`);
                    }
                  }}
                >
                  <div className="sermon-card-thumbnail">
                    {getThumbnail(sermon) ? (
                      <img
                        src={getThumbnail(sermon)}
                        alt={sermon.title}
                      />
                    ) : (
                      <div className="sermon-card-placeholder">
                        <span>🎧</span>
                      </div>
                    )}
                  </div>
                  <div className="sermon-card-info">
                    <h4 className="sermon-card-title">{sermon.title}</h4>
                    <p className="sermon-card-speaker">
                      {sermon.speaker}
                      {sermon.date && <> | {formatDate(sermon.date)}</>}
                    </p>
                    {sermon.series && (
                      <p className="sermon-card-series">{sermon.series}</p>
                    )}
                    {sermon.scripture && (
                      <p className="sermon-card-scripture">{sermon.scripture}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-results">No sermons found matching your search.</p>
            )}
          </div>
        )}

        {/* Fallback links */}
        <div className="sermons-fallback">
          <p>
            You can also visit our{" "}
            <a
              href="https://www.sermonaudio.com/broadcasters/crichbaptist/"
              target="_blank"
              rel="noopener noreferrer"
            >
              SermonAudio page
            </a>{" "}
            or{" "}
            <a
              href="https://www.youtube.com/@CrichBaptistchurch/videos"
              target="_blank"
              rel="noopener noreferrer"
            >
              YouTube channel
            </a>{" "}
            directly.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default OnlineSermons;
